export const APPSEC_COURSES = [
  {
    id: 'appsec-owasp-top10',
    title: 'Application Security for Developers (OWASP Top 10)',
    description: 'Practical, developer-focused security course covering the OWASP Top 10 with hands-on fixes and secure patterns.',
    level: 'intermediate',
    totalDuration: '12 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Basic web development', 'HTTP and REST basics'],
    learningOutcomes: [
      'Identify and remediate OWASP Top 10 risks',
      'Implement secure authentication and session management',
      'Prevent injection, XSS, CSRF, SSRF, and more',
      'Apply secure coding patterns in React/Node/Supabase',
      'Build defense-in-depth with headers and CSP',
    ],
    modules: [
      {
        id: 'owasp-top10-overview',
        title: 'OWASP Top 10 Overview & Quick Wins',
        description: 'Understand the OWASP Top 10 and implement quick, high-impact mitigations.',
        content: `# OWASP Top 10 Overview & Quick Security Wins

## Understanding the OWASP Top 10 2021

### A01: Broken Access Control
Access control failures allow attackers to bypass authorization and access unauthorized data or functionality.

**Quick Fixes:**
- Implement proper role-based access control (RBAC)
- Use least privilege principles
- Server-side validation for all access decisions
- Deny access by default

### A02: Cryptographic Failures
Failures related to cryptography that often expose sensitive data.

**Quick Fixes:**
- Use TLS 1.3 for data in transit
- Encrypt sensitive data at rest
- Use strong, up-to-date cryptographic algorithms
- Proper key management and rotation

### A03: Injection
Untrusted data sent to interpreters as part of commands or queries.

**Quick Fixes:**
- Use parameterized queries/prepared statements
- Input validation and sanitization
- Output encoding based on context
- Use ORMs with built-in protections

### A04: Insecure Design
Missing or ineffective control design flaws.

**Quick Fixes:**
- Threat modeling during design phase
- Secure design patterns and principles
- Defense in depth strategy
- Fail securely by default

### A05: Security Misconfiguration
Insecure default configurations, incomplete setups, and verbose error messages.

**Quick Fixes:**
- Remove default credentials and unnecessary features
- Implement security headers (HSTS, CSP, X-Frame-Options)
- Regular security configuration reviews
- Automated security testing in CI/CD

## Practical Implementation Guide

### Essential Security Headers
\`\`\`javascript
// Express.js security headers middleware
app.use((req, res, next) => {
  res.setHeader('X-Content-Type-Options', 'nosniff');
  res.setHeader('X-Frame-Options', 'DENY');
  res.setHeader('X-XSS-Protection', '1; mode=block');
  res.setHeader('Strict-Transport-Security', 'max-age=31536000; includeSubDomains');
  res.setHeader('Content-Security-Policy', "default-src 'self'");
  next();
});
\`\`\`

### SQL Injection Prevention
\`\`\`javascript
// ❌ Vulnerable code
const query = \`SELECT * FROM users WHERE id = \${userId}\`;

// ✅ Secure code with parameterized queries
const query = 'SELECT * FROM users WHERE id = ?';
db.execute(query, [userId]);
\`\`\`

### XSS Prevention
\`\`\`javascript
// ❌ Vulnerable code
document.innerHTML = userInput;

// ✅ Secure code with encoding
document.textContent = userInput; // or use DOMPurify for HTML
\`\`\`

## Developer Security Checklist
- [ ] All user inputs validated and sanitized
- [ ] Parameterized queries used for database access
- [ ] Security headers implemented
- [ ] Authentication and session management secure
- [ ] Sensitive data encrypted
- [ ] Error messages don't leak information
- [ ] Security testing integrated in CI/CD
- [ ] Dependencies regularly updated`,
        duration: '60 minutes',
        difficulty: 'intermediate',
        order: 1,
        estimatedTime: '60 minutes',
        objectives: [
          'Recognize each OWASP category',
          'Apply at least 5 quick-win mitigations',
        ],
        codeExamples: [
          {
            title: 'Secure Authentication Implementation',
            language: 'javascript',
            code: `// Secure authentication with bcrypt and JWT
const bcrypt = require('bcrypt');
const jwt = require('jsonwebtoken');

class AuthService {
  async hashPassword(password) {
    const saltRounds = 12;
    return await bcrypt.hash(password, saltRounds);
  }

  async validatePassword(password, hash) {
    return await bcrypt.compare(password, hash);
  }

  generateToken(userId) {
    return jwt.sign(
      { userId, iat: Date.now() },
      process.env.JWT_SECRET,
      { expiresIn: '1h' }
    );
  }

  // Secure cookie configuration
  setSecureCookie(res, token) {
    res.cookie('auth_token', token, {
      httpOnly: true,
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'strict',
      maxAge: 3600000 // 1 hour
    });
  }
}`,
            explanation: 'Demonstrates secure password hashing, JWT token generation, and secure cookie configuration.'
          }
        ],
        practice: {
          task: 'Harden a vulnerable web application by implementing OWASP Top 10 mitigations: fix SQL injection, prevent XSS, add security headers, and implement secure authentication.',
          starter: `// Vulnerable application code
app.post('/login', (req, res) => {
  const { username, password } = req.body;
  
  // Vulnerable SQL query
  const query = \`SELECT * FROM users WHERE username = '\${username}' AND password = '\${password}'\`;
  
  db.query(query, (err, results) => {
    if (results.length > 0) {
      // Insecure session management
      req.session.user = results[0];
      res.send(\`Welcome \${username}!\`);
    } else {
      res.send('Invalid credentials');
    }
  });
});

app.get('/profile', (req, res) => {
  // No access control
  const userId = req.query.id;
  const query = \`SELECT * FROM users WHERE id = \${userId}\`;
  
  db.query(query, (err, results) => {
    res.send(\`<h1>Profile: \${results[0].username}</h1>\`);
  });
});`,
          solution: `// Secured application code
const bcrypt = require('bcrypt');
const rateLimit = require('express-rate-limit');

// Rate limiting
const loginLimiter = rateLimit({
  windowMs: 15 * 60 * 1000, // 15 minutes
  max: 5, // limit each IP to 5 requests per windowMs
  message: 'Too many login attempts, please try again later'
});

// Security headers middleware
app.use((req, res, next) => {
  res.setHeader('X-Content-Type-Options', 'nosniff');
  res.setHeader('X-Frame-Options', 'DENY');
  res.setHeader('Content-Security-Policy', "default-src 'self'");
  next();
});

app.post('/login', loginLimiter, async (req, res) => {
  const { username, password } = req.body;
  
  // Input validation
  if (!username || !password) {
    return res.status(400).json({ error: 'Username and password required' });
  }
  
  try {
    // Parameterized query
    const query = 'SELECT * FROM users WHERE username = ?';
    const results = await db.execute(query, [username]);
    
    if (results.length > 0) {
      const user = results[0];
      const isValid = await bcrypt.compare(password, user.password_hash);
      
      if (isValid) {
        req.session.userId = user.id;
        req.session.role = user.role;
        res.json({ message: 'Login successful' });
      } else {
        res.status(401).json({ error: 'Invalid credentials' });
      }
    } else {
      res.status(401).json({ error: 'Invalid credentials' });
    }
  } catch (error) {
    console.error('Login error:', error);
    res.status(500).json({ error: 'Internal server error' });
  }
});

app.get('/profile', requireAuth, async (req, res) => {
  try {
    const userId = req.session.userId;
    const requestedId = req.query.id;
    
    // Access control - users can only view their own profile
    if (userId !== parseInt(requestedId) && req.session.role !== 'admin') {
      return res.status(403).json({ error: 'Access denied' });
    }
    
    const query = 'SELECT username, email FROM users WHERE id = ?';
    const results = await db.execute(query, [requestedId]);
    
    if (results.length > 0) {
      res.json(results[0]);
    } else {
      res.status(404).json({ error: 'User not found' });
    }
  } catch (error) {
    console.error('Profile error:', error);
    res.status(500).json({ error: 'Internal server error' });
  }
});

function requireAuth(req, res, next) {
  if (!req.session.userId) {
    return res.status(401).json({ error: 'Authentication required' });
  }
  next();
}`,
          hints: [
            'Use parameterized queries to prevent SQL injection',
            'Implement proper input validation and sanitization',
            'Add security headers using middleware',
            'Hash passwords with bcrypt and use secure session management',
            'Implement proper access controls and authorization',
            'Use rate limiting to prevent brute force attacks',
            'Handle errors securely without information disclosure'
          ],
          estimatedTime: 90,
        },
        quiz: {
          questions: [
            {
              id: 'appsec-q1',
              question: 'Which mitigation directly prevents SQL Injection?',
              options: ['CSP', 'Parameterized queries', 'X-Frame-Options', 'HSTS'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'appsec',
  },
  {
    id: 'secure-sdlc-devsecops',
    title: 'Secure SDLC & DevSecOps',
    description: 'Integrate security throughout the software development lifecycle with DevSecOps practices and automated security testing.',
    level: 'intermediate',
    totalDuration: '10 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['CI/CD pipeline experience', 'Basic security knowledge', 'Software development lifecycle understanding'],
    learningOutcomes: [
      'Implement security in CI/CD pipelines',
      'Configure SAST and DAST tools',
      'Establish security testing automation',
      'Manage vulnerabilities throughout SDLC',
      'Build security culture in development teams',
    ],
    modules: [
      {
        id: 'devsecops-fundamentals',
        title: 'DevSecOps Fundamentals',
        description: 'Understanding the principles and practices of DevSecOps.',
        content: `# DevSecOps Fundamentals\n\n## Core Principles\n- Shift-left security\n- Security as code\n- Continuous security monitoring\n- Collaborative security culture\n\n## Security in SDLC\n- Requirements and design phase security\n- Code review and static analysis\n- Dynamic testing and validation\n- Deployment and runtime security\n\n## Tools and Automation\n- Security scanning integration\n- Policy as code\n- Automated compliance checking\n- Security metrics and reporting`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 1,
        estimatedTime: '90 minutes',
        objectives: [
          'Understand DevSecOps principles',
          'Identify security touchpoints in SDLC',
          'Plan security automation strategy',
        ],
        practice: {
          task: 'Design a DevSecOps pipeline with security checkpoints at each stage.',
          hints: ['Include both automated and manual security gates', 'Consider fail-fast principles'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'devsecops-q1',
              question: 'What does "shift-left" mean in DevSecOps?',
              options: ['Moving security to production', 'Integrating security early in development', 'Shifting responsibility to ops team', 'Moving security to the left sidebar'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'sast-dast-implementation',
        title: 'SAST and DAST Implementation',
        description: 'Configure and integrate static and dynamic application security testing.',
        content: `# SAST and DAST Implementation\n\n## Static Application Security Testing (SAST)\n- Code analysis tools (SonarQube, Checkmarx)\n- Integration with IDEs and CI/CD\n- Custom rule configuration\n- False positive management\n\n## Dynamic Application Security Testing (DAST)\n- Web application scanners (OWASP ZAP, Burp)\n- API security testing\n- Automated security regression testing\n- Runtime vulnerability detection\n\n## Tool Integration\n- Pipeline integration strategies\n- Results correlation and deduplication\n- Security dashboards and reporting\n- Developer workflow integration`,
        duration: '120 minutes',
        difficulty: 'intermediate',
        order: 2,
        estimatedTime: '120 minutes',
        objectives: [
          'Configure SAST tools in development workflow',
          'Implement DAST for web applications',
          'Set up automated security testing pipelines',
        ],
        practice: {
          task: 'Integrate SAST and DAST tools into a CI/CD pipeline with proper reporting.',
          hints: ['Configure quality gates based on security findings', 'Set up developer feedback loops'],
          estimatedTime: 150,
        },
        quiz: {
          questions: [
            {
              id: 'sast-dast-q1',
              question: 'What is the main advantage of SAST over DAST?',
              options: ['Finds runtime vulnerabilities', 'Can analyze code without execution', 'Tests user interface', 'Requires less configuration'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'appsec',
  },
  {
    id: 'advanced-web-security-testing',
    title: 'Advanced Web Security Testing',
    description: 'Master penetration testing, security automation, and bug bounty hunting techniques for web applications.',
    level: 'advanced',
    totalDuration: '12 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Web application fundamentals', 'Basic penetration testing', 'Understanding of common vulnerabilities'],
    learningOutcomes: [
      'Perform comprehensive web application penetration testing',
      'Master advanced Burp Suite techniques',
      'Automate security testing with custom scripts',
      'Apply bug bounty hunting methodologies',
      'Develop custom security testing tools',
    ],
    modules: [
      {
        id: 'advanced-pentest-methodology',
        title: 'Advanced Penetration Testing Methodology',
        description: 'Comprehensive approach to web application security testing.',
        content: `# Advanced Penetration Testing\n\n## Testing Methodology\n- Information gathering and reconnaissance\n- Attack surface mapping\n- Vulnerability identification and exploitation\n- Post-exploitation and impact assessment\n\n## Advanced Techniques\n- Blind SQL injection exploitation\n- XXE and SSRF exploitation\n- Business logic vulnerability testing\n- Client-side attack vectors\n\n## Reporting and Communication\n- Professional security assessment reports\n- Risk rating and business impact\n- Remediation recommendations\n- Executive summary presentation`,
        duration: '150 minutes',
        difficulty: 'advanced',
        order: 1,
        estimatedTime: '150 minutes',
        objectives: [
          'Execute systematic penetration testing',
          'Identify complex vulnerability chains',
          'Produce professional security reports',
        ],
        practice: {
          task: 'Perform comprehensive penetration test on a vulnerable web application.',
          hints: ['Document all findings with proof-of-concept', 'Consider business impact in reporting'],
          estimatedTime: 180,
        },
        quiz: {
          questions: [
            {
              id: 'pentest-q1',
              question: 'What is the primary goal of post-exploitation in penetration testing?',
              options: ['Fix vulnerabilities', 'Determine impact and business risk', 'Delete sensitive data', 'Install backdoors'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'bug-bounty-hunting',
        title: 'Bug Bounty Hunting Techniques',
        description: 'Learn professional bug bounty hunting methodologies and tools.',
        content: `# Bug Bounty Hunting\n\n## Hunting Methodology\n- Target selection and scope analysis\n- Automated reconnaissance\n- Manual testing techniques\n- Vulnerability chaining\n\n## Tools and Automation\n- Custom reconnaissance scripts\n- Automated vulnerability scanners\n- Fuzzing techniques\n- Report automation\n\n## Professional Practices\n- Responsible disclosure\n- Writing effective bug reports\n- Building relationships with vendors\n- Time management and efficiency`,
        duration: '150 minutes',
        difficulty: 'advanced',
        order: 2,
        estimatedTime: '150 minutes',
        objectives: [
          'Develop efficient hunting methodologies',
          'Build custom testing automation',
          'Write professional vulnerability reports',
        ],
        practice: {
          task: 'Create an automated reconnaissance and testing workflow for bug bounty hunting.',
          hints: ['Focus on efficiency and repeatability', 'Include proper documentation'],
          estimatedTime: 180,
        },
        quiz: {
          questions: [
            {
              id: 'bugbounty-q1',
              question: 'What is the most important aspect of a good bug bounty report?',
              options: ['Technical complexity', 'Clear reproduction steps', 'Number of vulnerabilities', 'Advanced exploitation'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'appsec',
  },
  {
    id: 'cloud-security-architecture',
    title: 'Cloud Security Architecture',
    description: 'Design and implement secure cloud architectures with focus on AWS, Azure security, and Zero Trust principles.',
    level: 'advanced',
    totalDuration: '8 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Cloud platform experience', 'Network security knowledge', 'Identity and access management basics'],
    learningOutcomes: [
      'Design secure cloud architectures',
      'Implement Zero Trust security models',
      'Configure cloud-native security services',
      'Manage identity and access at scale',
      'Implement container and serverless security',
    ],
    modules: [
      {
        id: 'zero-trust-architecture',
        title: 'Zero Trust Security Architecture',
        description: 'Implement Zero Trust principles in cloud environments.',
        content: `# Zero Trust Architecture\n\n## Core Principles\n- Never trust, always verify\n- Principle of least privilege\n- Assume breach mentality\n- Continuous verification\n\n## Implementation Components\n- Identity-based access control\n- Micro-segmentation\n- Continuous monitoring\n- Data protection and encryption\n\n## Cloud Implementation\n- Identity providers and SSO\n- Network security groups\n- API security and OAuth\n- Conditional access policies`,
        duration: '120 minutes',
        difficulty: 'advanced',
        order: 1,
        estimatedTime: '120 minutes',
        objectives: [
          'Design Zero Trust architecture',
          'Implement identity-based access controls',
          'Configure micro-segmentation',
        ],
        practice: {
          task: 'Design and implement Zero Trust architecture for a multi-tier cloud application.',
          hints: ['Focus on identity and network segmentation', 'Include monitoring and compliance'],
          estimatedTime: 150,
        },
        quiz: {
          questions: [
            {
              id: 'zerotrust-q1',
              question: 'What is the fundamental principle of Zero Trust?',
              options: ['Trust but verify', 'Never trust, always verify', 'Trust internal networks', 'Verify once, trust always'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'container-serverless-security',
        title: 'Container and Serverless Security',
        description: 'Secure containerized applications and serverless functions.',
        content: `# Container and Serverless Security\n\n## Container Security\n- Image vulnerability scanning\n- Runtime security monitoring\n- Secrets management\n- Network policies and isolation\n\n## Serverless Security\n- Function permissions and IAM\n- Input validation and injection prevention\n- Cold start security considerations\n- Monitoring and logging\n\n## Security Tools\n- Twistlock/Prisma Cloud\n- Aqua Security\n- AWS Security Hub\n- Azure Security Center`,
        duration: '120 minutes',
        difficulty: 'advanced',
        order: 2,
        estimatedTime: '120 minutes',
        objectives: [
          'Implement container security best practices',
          'Secure serverless applications',
          'Configure cloud-native security tools',
        ],
        practice: {
          task: 'Secure a containerized microservices application with serverless components.',
          hints: ['Use least privilege principles', 'Implement proper secrets management'],
          estimatedTime: 150,
        },
        quiz: {
          questions: [
            {
              id: 'container-security-q1',
              question: 'What is the most critical security practice for container images?',
              options: ['Using latest tags', 'Regular vulnerability scanning', 'Large base images', 'Root user access'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'appsec',
  },
  {
    id: 'threat-modeling-risk-assessment',
    title: 'Threat Modeling & Risk Assessment',
    description: 'Master systematic threat modeling methodologies and enterprise risk assessment for application security.',
    level: 'intermediate',
    totalDuration: '6 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Security fundamentals', 'Software architecture knowledge', 'Basic risk management concepts'],
    learningOutcomes: [
      'Apply STRIDE methodology for threat modeling',
      'Conduct comprehensive security architecture reviews',
      'Perform quantitative and qualitative risk assessments',
      'Create actionable security recommendations',
      'Integrate threat modeling into development processes',
    ],
    modules: [
      {
        id: 'stride-methodology',
        title: 'STRIDE Threat Modeling Methodology',
        description: 'Learn and apply the STRIDE methodology for systematic threat identification.',
        content: `# STRIDE Methodology\n\n## STRIDE Categories\n- Spoofing identity\n- Tampering with data\n- Repudiation\n- Information disclosure\n- Denial of service\n- Elevation of privilege\n\n## Threat Modeling Process\n- System decomposition and data flow diagrams\n- Trust boundary identification\n- Threat enumeration using STRIDE\n- Vulnerability analysis and impact assessment\n\n## Tools and Techniques\n- Microsoft Threat Modeling Tool\n- Data flow diagram creation\n- Attack tree analysis\n- Security control mapping`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 1,
        estimatedTime: '90 minutes',
        objectives: [
          'Create comprehensive data flow diagrams',
          'Apply STRIDE methodology systematically',
          'Identify and prioritize security threats',
        ],
        practice: {
          task: 'Perform STRIDE threat modeling for a web application with database backend.',
          hints: ['Focus on trust boundaries and data flows', 'Consider both technical and business threats'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'stride-q1',
              question: 'In STRIDE, what does the "T" represent?',
              options: ['Trust', 'Tampering', 'Threat', 'Technical'],
              correctAnswer: 1,
            },
          ],
        },
      },
      {
        id: 'enterprise-risk-assessment',
        title: 'Enterprise Risk Assessment',
        description: 'Conduct comprehensive risk assessments for enterprise security programs.',
        content: `# Enterprise Risk Assessment\n\n## Risk Assessment Frameworks\n- NIST Risk Management Framework\n- ISO 27005 risk management\n- FAIR (Factor Analysis of Information Risk)\n- OCTAVE methodology\n\n## Quantitative vs Qualitative\n- Risk matrices and heat maps\n- Monte Carlo simulation\n- Business impact analysis\n- Cost-benefit analysis\n\n## Risk Treatment\n- Risk mitigation strategies\n- Residual risk acceptance\n- Risk transfer and insurance\n- Continuous monitoring`,
        duration: '90 minutes',
        difficulty: 'intermediate',
        order: 2,
        estimatedTime: '90 minutes',
        objectives: [
          'Conduct systematic risk assessments',
          'Calculate risk metrics and priorities',
          'Develop risk treatment plans',
        ],
        practice: {
          task: 'Perform comprehensive risk assessment for an enterprise application portfolio.',
          hints: ['Consider business impact and likelihood', 'Include regulatory and compliance risks'],
          estimatedTime: 120,
        },
        quiz: {
          questions: [
            {
              id: 'risk-q1',
              question: 'What is the primary goal of risk assessment?',
              options: ['Eliminate all risks', 'Identify and prioritize risks', 'Implement security controls', 'Reduce costs'],
              correctAnswer: 1,
            },
          ],
        },
      },
    ],
    type: 'appsec',
  },
];
